// -----------------------------------------------------------
// FloraIQ — Aba Visão Geral da Qualidade
// -----------------------------------------------------------
// Tudo aqui vem de GET /quality/issues e GET /quality/summary.

import type { Inconsistency } from "@/types/ui-models";
import { useQualityRankingList } from "@/hooks";
import { NoRealData } from "@/components/shared";
import {
  QUALITY_RULES,
  QUALITY_RULE_GROUP_ICONS,
  QUALITY_RULE_GROUP_LABELS,
  type QualityRuleGroup,
} from "@/config/qualidade-3d-config";

interface QualityOverviewProps {
  issues: Inconsistency[];
  loading: boolean;
  error: string | null;
}

const GROUPS = Object.keys(QUALITY_RULE_GROUP_LABELS) as QualityRuleGroup[];

export default function QualityOverview({ issues, loading, error }: QualityOverviewProps) {
  const ranking = useQualityRankingList();

  const openByGroup = GROUPS.map((g) => {
    const codes = QUALITY_RULES.filter((r) => r.group === g).map((r) => r.code);
    const groupIssues = issues.filter((i) => codes.includes(i.regra));
    return {
      group: g,
      rules: codes.length,
      open: groupIssues.length,
      critical: groupIssues.filter((i) => i.severidade === "crítica").length,
    };
  });

  const groupValue = (n: number) => (loading ? "…" : error ? "—" : n.toLocaleString("pt-BR"));

  return (
    <div className="space-y-6">
      {/* Pendencias por grupo */}
      <div>
        <h4 className="text-sm font-semibold text-foreground-800 mb-3">Inconsistências abertas por grupo</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {openByGroup.map((g) => (
            <div key={g.group} className="bg-white rounded-card border border-background-200/70 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-100 text-blue-600">
                  <i className={`${QUALITY_RULE_GROUP_ICONS[g.group]} text-sm`}></i>
                </div>
                <span className="text-xs font-medium text-foreground-600 truncate">{QUALITY_RULE_GROUP_LABELS[g.group]}</span>
              </div>
              <div className="flex items-end justify-between">
                <span className="text-xl font-bold font-heading text-foreground-900">{groupValue(g.open)}</span>
                <span className="text-xs text-foreground-500">
                  {g.rules} {g.rules === 1 ? "regra" : "regras"}
                  {!loading && !error && g.critical > 0 && ` · ${g.critical} crítica(s)`}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <NoRealData
          title="Evolução da qualidade"
          icon="ri-line-chart-line"
          description="As inconsistências são calculadas na hora pela view do banco e não há histórico gravado. A série temporal depende de registrar o resultado de cada validação."
        />

        {/* Ranking de projetos */}
        <div className="bg-white rounded-card border border-background-200/70 p-4">
          <h4 className="text-sm font-semibold text-foreground-800 mb-1">Projetos por pendências</h4>
          <p className="text-xs text-foreground-500 mb-3">
            Índice estimado pela view: 100 menos 5 pontos por inconsistência aberta.
          </p>
          {ranking.loading ? (
            <p className="text-xs text-foreground-500 py-6 text-center">Carregando...</p>
          ) : ranking.error ? (
            <p className="text-xs text-red-600 py-6 text-center">Não foi possível carregar: {ranking.error}</p>
          ) : ranking.items.length === 0 ? (
            <p className="text-xs text-foreground-500 py-6 text-center">Nenhum projeto avaliado.</p>
          ) : (
            <div className="space-y-2">
              {[...ranking.items]
                .sort((a, b) => a.score - b.score)
                .map((p, i) => (
                  <div key={`${p.projeto}-${i}`} className="flex items-center gap-3 py-2 border-b border-background-100 last:border-0">
                    <span className="text-xs font-bold text-foreground-400 w-5">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground-700 truncate">{p.projeto}</p>
                      <p className="text-xs text-foreground-500">{p.inconsistencias.toLocaleString("pt-BR")} pendências abertas</p>
                    </div>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                      p.status === "excelente" ? "bg-green-100 text-green-700" :
                      p.status === "bom" ? "bg-blue-100 text-blue-700" :
                      p.status === "regular" ? "bg-yellow-100 text-yellow-700" :
                      "bg-red-100 text-red-700"
                    }`}>{p.score}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
